import { useState, type ReactNode } from 'react';
import { criarCompraSchema, type AtualizarCompraDto, type CompraMpResponse, type CriarCompraDto, type StatusEntregaCompra, type StatusPagamentoCompra } from '@triade/contracts';
import { useFornecedores } from '@/api/fornecedores';
import { useItens } from '@/api/itens';
import { usePode } from '@/api/me';
import { useAtualizarCompra, useCriarCompra } from '@/api/plano';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarMoeda } from '@/lib/format';
import { ENTREGAS_ORDEM, PAGAMENTOS_ORDEM, ROTULO_ENTREGA, ROTULO_PAGAMENTO } from './compras-busca';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

function Campo({ id, rotulo, erro, children, className }: { id: string; rotulo: string; erro?: string | null; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label htmlFor={id} className={ROTULO}>
        {rotulo}
      </label>
      {children}
      {erro && (
        <p role="alert" className="mt-1 text-caption text-danger-ink">
          {erro}
        </p>
      )}
    </div>
  );
}

// ---------- Formulario: so texto <-> string decimal da API. Nenhum valor e calculado aqui (o total vem da resposta). ----------

export interface CompraForm {
  fornecedorId: string;
  item: ItemResumo | null;
  numeroPedido: string;
  nf: string;
  emissao: string;
  quantidade: string;
  precoUnitario: string;
  dataEntrega: string;
  condicaoPagamento: string;
  dataPagamento: string;
  statusEntrega: StatusEntregaCompra;
  statusPagamento: StatusPagamentoCompra;
  tipoProduto: string;
  observacao: string;
}

/** Compra nova: entrega no primeiro dia do mes da tela. */
export function compraFormVazio(mes: string): CompraForm {
  return {
    fornecedorId: '',
    item: null,
    numeroPedido: '',
    nf: '',
    emissao: '',
    quantidade: '',
    precoUnitario: '',
    dataEntrega: `${mes}-01`,
    condicaoPagamento: '',
    dataPagamento: '',
    statusEntrega: 'A_ENTREGAR',
    statusPagamento: 'A_PAGAR',
    tipoProduto: '',
    observacao: '',
  };
}

/** Compra existente: valores da API no texto do campo (pt-BR, sem arredondar). */
export function compraFormDe(c: CompraMpResponse): CompraForm {
  return {
    fornecedorId: c.fornecedorId ?? '',
    item: { id: c.itemId, codigo: c.itemCodigo, descricao: c.itemDescricao },
    numeroPedido: c.numeroPedido ?? '',
    nf: c.nf ?? '',
    emissao: c.emissao ?? '',
    quantidade: decimalParaTextoCampo(c.quantidade),
    precoUnitario: decimalParaTextoCampo(c.precoUnitario),
    dataEntrega: c.dataEntrega ?? '',
    condicaoPagamento: c.condicaoPagamento ?? '',
    dataPagamento: c.dataPagamento ?? '',
    statusEntrega: c.statusEntrega,
    statusPagamento: c.statusPagamento,
    tipoProduto: c.tipoProduto ?? '',
    observacao: c.observacao ?? '',
  };
}

export type CampoCompra = 'fornecedorId' | 'itemId' | 'quantidade' | 'precoUnitario' | 'emissao' | 'dataEntrega' | 'dataPagamento' | 'geral';

export type MontagemCompra = { ok: true; corpo: CriarCompraDto } | { ok: false; campo: CampoCompra; mensagem: string };

const CAMPO_DO_SCHEMA: Record<string, CampoCompra> = {
  fornecedorId: 'fornecedorId',
  itemId: 'itemId',
  quantidade: 'quantidade',
  precoUnitario: 'precoUnitario',
  emissao: 'emissao',
  dataEntrega: 'dataEntrega',
  dataPagamento: 'dataPagamento',
};

const opcional = (s: string): string | null => (s.trim() ? s.trim() : null);

/** Texto do formulario para o corpo da API (validado pelo schema compartilhado). Sem dados bancarios. */
export function montarCorpoCompra(f: CompraForm): MontagemCompra {
  const erro = (campo: CampoCompra, mensagem: string): MontagemCompra => ({ ok: false, campo, mensagem });
  if (!f.item) return erro('itemId', 'Escolha o produto');
  const quantidade = decimalDigitado(f.quantidade);
  if (quantidade === null) return erro('quantidade', 'Informe a quantidade em toneladas (t)');
  const precoUnitario = decimalDigitado(f.precoUnitario);
  if (precoUnitario === null) return erro('precoUnitario', 'Informe o preço por unidade');
  const corpo = {
    fornecedorId: f.fornecedorId || null,
    itemId: f.item.id,
    quantidade,
    precoUnitario,
    numeroPedido: opcional(f.numeroPedido),
    nf: opcional(f.nf),
    emissao: opcional(f.emissao),
    dataEntrega: opcional(f.dataEntrega),
    condicaoPagamento: opcional(f.condicaoPagamento),
    dataPagamento: opcional(f.dataPagamento),
    statusEntrega: f.statusEntrega,
    statusPagamento: f.statusPagamento,
    tipoProduto: opcional(f.tipoProduto),
    observacao: opcional(f.observacao),
  };
  const valido = criarCompraSchema.safeParse(corpo);
  if (!valido.success) {
    const issue = valido.error.issues[0];
    const chave = String(issue?.path[0] ?? '');
    return erro(CAMPO_DO_SCHEMA[chave] ?? 'geral', issue?.message ?? 'Dados da compra inválidos');
  }
  return { ok: true, corpo: valido.data };
}

/**
 * Compra de MP (nova ou existente). Sem campos de banco, agencia, conta, PIX ou e-mail (o cadastro nao os tem).
 * O total e a quantidade exibidos depois de salvar vem da resposta da API.
 */
export function CompraDialog({ compra, mes, onFechar }: { compra: CompraMpResponse | null; mes: string; onFechar: () => void }) {
  const { pode } = usePode();
  const criar = useCriarCompra();
  const atualizar = useAtualizarCompra();
  // Fornecedores exigem custo.ver na API: sem a permissao a lista nao e consultada.
  const fornecedores = useFornecedores({ limit: 200 }, { ativo: pode('custo.ver') });
  const [termo, setTermo] = useState('');
  const termoAtrasado = useDebounced(termo.trim(), 250);
  const itens = useItens({ ativo: true, limit: 20, ...(termoAtrasado ? { q: termoAtrasado } : {}) });
  const [form, setForm] = useState<CompraForm>(() => (compra ? compraFormDe(compra) : compraFormVazio(mes)));
  const [erroCampo, setErroCampo] = useState<{ campo: CampoCompra; mensagem: string } | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salva, setSalva] = useState<CompraMpResponse | null>(null);
  const salvando = criar.isPending || atualizar.isPending;

  const set = <K extends keyof CompraForm>(chave: K, valor: CompraForm[K]) => setForm((f) => ({ ...f, [chave]: valor }));
  const erroDe = (campo: CampoCompra) => (erroCampo?.campo === campo ? erroCampo.mensagem : null);

  // Fornecedor fora da lista (inativo, ou lista sem permissao) entra como opcao para a compra existente.
  const opcoesFornecedor = (() => {
    const lista = (fornecedores.data ?? []).map((f) => ({ id: f.id, nome: f.nome }));
    if (compra?.fornecedorId && compra.fornecedorNome && !lista.some((f) => f.id === compra.fornecedorId)) lista.push({ id: compra.fornecedorId, nome: compra.fornecedorNome });
    return lista;
  })();

  async function gravar() {
    setErroGeral(null);
    setErroCampo(null);
    const montado = montarCorpoCompra(form);
    if (!montado.ok) {
      setErroCampo({ campo: montado.campo, mensagem: montado.mensagem });
      return;
    }
    try {
      const resposta = compra
        ? await atualizar.mutateAsync({ id: compra.id, dto: montado.corpo as AtualizarCompraDto })
        : await criar.mutateAsync(montado.corpo);
      setSalva(resposta);
    } catch (e) {
      setErroGeral(mensagemApi(e));
      if (e instanceof ApiError && e.code === 'SEM_CUSTO') setErroCampo({ campo: 'itemId', mensagem: 'Este produto não tem custo na data de entrega.' });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !salvando && onFechar()}>
      <DialogContent className="max-h-[92vh] max-w-[720px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{compra ? (compra.numeroPedido ? `Compra ${compra.numeroPedido}` : 'Compra de MP') : 'Nova compra de MP'}</DialogTitle>
          <DialogDescription>Quantidade em toneladas (t). Preço por unidade de compra (kg), como no pedido do fornecedor.</DialogDescription>
        </DialogHeader>

        {salva ? (
          <div role="status" className="flex flex-col gap-3 rounded-xl border border-border bg-surface-muted p-4 text-small">
            <p className="font-semibold">Compra salva.</p>
            <p>
              Total da compra: <span className="font-semibold tabular-nums">{formatarMoeda(salva.total)}</span>
            </p>
            <DialogFooter>
              <Button onClick={onFechar} className="h-[38px] rounded-[10px] px-4">
                Concluir
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {erroGeral && (
              <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
                Não foi possível salvar: {erroGeral}
              </div>
            )}
            <fieldset disabled={salvando} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo id="compra-fornecedor" rotulo="Fornecedor" erro={erroDe('fornecedorId')}>
                <select id="compra-fornecedor" className={CAMPO} value={form.fornecedorId} aria-invalid={erroDe('fornecedorId') ? true : undefined} onChange={(e) => set('fornecedorId', e.target.value)}>
                  <option value="">Sem fornecedor</option>
                  {opcoesFornecedor.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
              </Campo>

              <div>
                <span className={ROTULO}>
                  <label htmlFor="compra-item">Produto (matéria-prima)</label>
                </span>
                <ItemCombobox
                  id="compra-item"
                  valor={form.item}
                  onSelecionar={(it: ItemResumo) => set('item', it)}
                  itens={itens.data ?? []}
                  carregando={itens.isFetching}
                  erro={itens.error?.message ?? null}
                  termo={termo}
                  onTermoChange={setTermo}
                />
                {erroDe('itemId') && (
                  <p role="alert" className="mt-1 text-caption text-danger-ink">
                    {erroDe('itemId')}
                  </p>
                )}
              </div>

              <Campo id="compra-quantidade" rotulo="Quantidade (t)" erro={erroDe('quantidade')}>
                <input id="compra-quantidade" inputMode="decimal" className={CAMPO} value={form.quantidade} aria-invalid={erroDe('quantidade') ? true : undefined} onChange={(e) => set('quantidade', e.target.value)} />
              </Campo>

              <Campo id="compra-preco" rotulo="Preço por unidade (R$)" erro={erroDe('precoUnitario')}>
                <input id="compra-preco" inputMode="decimal" className={CAMPO} value={form.precoUnitario} aria-invalid={erroDe('precoUnitario') ? true : undefined} onChange={(e) => set('precoUnitario', e.target.value)} />
              </Campo>

              <Campo id="compra-numero" rotulo="Nº do pedido ao fornecedor">
                <input id="compra-numero" className={CAMPO} value={form.numeroPedido} onChange={(e) => set('numeroPedido', e.target.value)} />
              </Campo>

              <Campo id="compra-nf" rotulo="Nota fiscal">
                <input id="compra-nf" className={CAMPO} value={form.nf} onChange={(e) => set('nf', e.target.value)} />
              </Campo>

              <Campo id="compra-emissao" rotulo="Data de emissão" erro={erroDe('emissao')}>
                <input id="compra-emissao" type="date" className={CAMPO} value={form.emissao} onChange={(e) => set('emissao', e.target.value)} />
              </Campo>

              <Campo id="compra-entrega" rotulo="Previsão de entrega" erro={erroDe('dataEntrega')}>
                <input id="compra-entrega" type="date" className={CAMPO} value={form.dataEntrega} onChange={(e) => set('dataEntrega', e.target.value)} />
              </Campo>

              <Campo id="compra-status-entrega" rotulo="Status da entrega">
                <select id="compra-status-entrega" className={CAMPO} value={form.statusEntrega} onChange={(e) => set('statusEntrega', e.target.value as StatusEntregaCompra)}>
                  {ENTREGAS_ORDEM.map((s) => (
                    <option key={s} value={s}>
                      {ROTULO_ENTREGA[s]}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo id="compra-status-pagamento" rotulo="Pagamento">
                <select id="compra-status-pagamento" className={CAMPO} value={form.statusPagamento} onChange={(e) => set('statusPagamento', e.target.value as StatusPagamentoCompra)}>
                  {PAGAMENTOS_ORDEM.map((s) => (
                    <option key={s} value={s}>
                      {ROTULO_PAGAMENTO[s]}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo id="compra-data-pagamento" rotulo="Data do pagamento" erro={erroDe('dataPagamento')}>
                <input id="compra-data-pagamento" type="date" className={CAMPO} value={form.dataPagamento} onChange={(e) => set('dataPagamento', e.target.value)} />
              </Campo>

              <Campo id="compra-condicao" rotulo="Condição de pagamento">
                <input id="compra-condicao" className={CAMPO} value={form.condicaoPagamento} onChange={(e) => set('condicaoPagamento', e.target.value)} />
              </Campo>

              <Campo id="compra-tipo" rotulo="Tipo de produto">
                <input id="compra-tipo" className={CAMPO} value={form.tipoProduto} onChange={(e) => set('tipoProduto', e.target.value)} />
              </Campo>

              <Campo id="compra-observacao" rotulo="Observação" className="sm:col-span-2">
                <textarea id="compra-observacao" rows={2} className="w-full rounded-[9px] border border-input bg-card px-2.5 py-2 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none" value={form.observacao} onChange={(e) => set('observacao', e.target.value)} />
              </Campo>
            </fieldset>

            <DialogFooter>
              <Button variant="outline" onClick={onFechar} disabled={salvando} className="h-[38px] rounded-[10px] px-4">
                Cancelar
              </Button>
              <Button onClick={() => void gravar()} disabled={salvando} className="h-[38px] rounded-[10px] px-4">
                {salvando ? 'Salvando…' : 'Salvar compra'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
